import React, { useState, useEffect, useRef } from 'react';

const FocusMode = ({ isActive, onToggle }) => {
  const [settings, setSettings] = useState({
    blockingEnabled: false,
    ambientSound: 'none',
    soundVolume: 0.5,
    theme: 'default',
    showMotivationalQuotes: true,
    dimBackground: false
  });
  const [currentQuote, setCurrentQuote] = useState('');
  const audioRef = useRef(null);

  const ambientSounds = [
    { id: 'none', name: 'None', file: null },
    { id: 'rain', name: '🌧️ Rain', file: 'rain.mp3' },
    { id: 'forest', name: '🌲 Forest', file: 'forest.mp3' },
    { id: 'coffee', name: '☕ Coffee Shop', file: 'coffee.mp3' },
    { id: 'ocean', name: '🌊 Ocean Waves', file: 'ocean.mp3' },
    { id: 'whitenoise', name: '⚪ White Noise', file: 'whitenoise.mp3' },
    { id: 'library', name: '📚 Library', file: 'library.mp3' }
  ];

  const themes = [
    { id: 'default', name: 'Default', colors: { primary: '#667eea', secondary: '#764ba2' } },
    { id: 'forest', name: '🌲 Forest', colors: { primary: '#56ab2f', secondary: '#a8e6cf' } },
    { id: 'ocean', name: '🌊 Ocean', colors: { primary: '#006994', secondary: '#38a3d1' } },
    { id: 'sunset', name: '🌅 Sunset', colors: { primary: '#ff6b6b', secondary: '#ffa726' } },
    { id: 'night', name: '🌙 Night', colors: { primary: '#2c3e50', secondary: '#4a6741' } },
    { id: 'minimal', name: '⚫ Minimal', colors: { primary: '#2d3436', secondary: '#636e72' } }
  ];

  const motivationalQuotes = [
    "The expert in anything was once a beginner.",
    "Focus on being productive instead of busy.",
    "Success is the sum of small efforts repeated day in and day out.",
    "Don't watch the clock; do what it does. Keep going.",
    "The future depends on what you do today.",
    "You don't have to be great to get started, but you have to get started to be great.",
    "Small progress is still progress.",
    "Believe you can and you're halfway there.",
    "The only impossible journey is the one you never begin.",
    "Your limitation—it's only your imagination.",
    "Great things never come from comfort zones.",
    "Dream it. Wish it. Do it.",
    "Success doesn't just find you. You have to go out and get it.",
    "The harder you work for something, the greater you'll feel when you achieve it.",
    "Don't stop when you're tired. Stop when you're done."
  ];

  const blockedSites = [
    'facebook.com', 'twitter.com', 'instagram.com', 'youtube.com',
    'netflix.com', 'reddit.com', 'tiktok.com', 'snapchat.com',
    'pinterest.com', 'linkedin.com', 'twitch.tv', 'discord.com'
  ];

  useEffect(() => {
    // Load settings from localStorage
    const savedSettings = localStorage.getItem('focusModeSettings');
    if (savedSettings) {
      setSettings(JSON.parse(savedSettings));
    }
  }, []);

  useEffect(() => {
    // Save settings to localStorage
    localStorage.setItem('focusModeSettings', JSON.stringify(settings));

    // Apply theme
    if (isActive) {
      const theme = themes.find(t => t.id === settings.theme);
      if (theme) {
        document.documentElement.style.setProperty('--primary-color', theme.colors.primary);
        document.documentElement.style.setProperty('--secondary-color', theme.colors.secondary);
      }

      // Apply background dimming
      if (settings.dimBackground) {
        document.body.classList.add('focus-mode-dimmed');
      }
    } else {
      // Reset theme when focus mode is disabled
      document.documentElement.style.removeProperty('--primary-color');
      document.documentElement.style.removeProperty('--secondary-color');
      document.body.classList.remove('focus-mode-dimmed');
    }

    return () => {
      document.body.classList.remove('focus-mode-dimmed');
    };
  }, [settings, isActive]);

  useEffect(() => {
    // Handle ambient sounds
    if (isActive && settings.ambientSound !== 'none') {
      // Since we can't load actual audio files, we'll simulate with audio context
      // In a real app, you would load actual ambient sound files
      playAmbientSound();
    } else {
      stopAmbientSound();
    }

    return () => stopAmbientSound();
  }, [isActive, settings.ambientSound, settings.soundVolume]);

  useEffect(() => {
    // Show motivational quotes
    if (isActive && settings.showMotivationalQuotes) {
      showRandomQuote();
      const interval = setInterval(showRandomQuote, 300000); // Every 5 minutes
      return () => clearInterval(interval);
    }
  }, [isActive, settings.showMotivationalQuotes]);

  const playAmbientSound = () => {
    // Simulate ambient sound with Web Audio API
    try {
      if (!audioRef.current) {
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();

        // Create different types of ambient sounds
        switch (settings.ambientSound) {
          case 'rain':
            // Simulate rain with filtered noise
            oscillator.type = 'sawtooth';
            oscillator.frequency.setValueAtTime(100, audioContext.currentTime);
            break;
          case 'ocean':
            oscillator.type = 'sine';
            oscillator.frequency.setValueAtTime(50, audioContext.currentTime);
            break;
          case 'whitenoise':
            oscillator.type = 'sawtooth';
            oscillator.frequency.setValueAtTime(200, audioContext.currentTime);
            break;
          default:
            oscillator.type = 'sine';
            oscillator.frequency.setValueAtTime(80, audioContext.currentTime);
        }

        gainNode.gain.setValueAtTime(settings.soundVolume * 0.1, audioContext.currentTime);
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        oscillator.start();

        audioRef.current = { oscillator, gainNode, audioContext };
      }
    } catch (error) {
      console.log('Audio not available');
    }
  };

  const stopAmbientSound = () => {
    if (audioRef.current) {
      try {
        audioRef.current.oscillator.stop();
        audioRef.current.audioContext.close();
        audioRef.current = null;
      } catch (error) {
        console.log('Error stopping audio');
      }
    }
  };

  const showRandomQuote = () => {
    const randomQuote = motivationalQuotes[Math.floor(Math.random() * motivationalQuotes.length)];
    setCurrentQuote(randomQuote);
  };

  const updateSettings = (newSettings) => {
    setSettings(newSettings);
  };

  const BlockedSitesList = () => (
    <div className="blocked-sites">
      <h4>🚫 Blocked Sites</h4>
      <div className="sites-grid">
        {blockedSites.map(site => (
          <div key={site} className="blocked-site">
            {site}
          </div>
        ))}
      </div>
      <p className="blocking-note">
        * Website blocking is simulated. In a full app, this would integrate with browser extensions or system-level blocking.
      </p>
    </div>
  );

  return (
    <div className="focus-mode">
      <div className="card">
        <div className="page-header">
          <h2 className="page-title">
            🎯 Focus Mode
          </h2>
          <button
            onClick={onToggle}
            className={`btn btn-lg ${isActive ? 'btn-danger' : 'btn-success'}`}
          >
            {isActive ? '🔴 Exit Focus Mode' : '🟢 Enter Focus Mode'}
          </button>
        </div>

        {isActive && currentQuote && settings.showMotivationalQuotes && (
          <div className="card quote-card"
            style={{ textAlign: 'center', marginBottom: '2rem' }}>
            <div className="quote-text">
              <span className="quote-mark">"</span>
              {currentQuote}
              <span className="quote-mark">"</span>
            </div>
          </div>
        )}
      </div>

      <div className="card">
        <h3 className="section-title">
          ⚙️ Focus Settings
        </h3>

        <div className="setting-section">
          <h4 className="subsection-title">
            🎨 Theme
          </h4>
          <div className="theme-buttons">
            {themes.map(theme => (
              <button
                key={theme.id}
                onClick={() => updateSettings({ ...settings, theme: theme.id })}
                className={`theme-btn ${theme.id} ${settings.theme === theme.id ? 'active' : ''}`}
              >
                {theme.name}
              </button>
            ))}
          </div>
        </div>

        <div className="setting-section">
          <h4 className="subsection-title">🔊 Ambient Sounds</h4>
          <div className="sound-controls">
            <select
              value={settings.ambientSound}
              onChange={(e) => updateSettings({ ...settings, ambientSound: e.target.value })}
              className="sound-select"
            >
              {ambientSounds.map(sound => (
                <option key={sound.id} value={sound.id}>
                  {sound.name}
                </option>
              ))}
            </select>

            {settings.ambientSound !== 'none' && (
              <div className="volume-control">
                <label>Volume:</label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={settings.soundVolume}
                  onChange={(e) => updateSettings({ ...settings, soundVolume: parseFloat(e.target.value) })}
                  className="volume-slider"
                />
                <span>{Math.round(settings.soundVolume * 100)}%</span>
              </div>
            )}
          </div>
        </div>

        <div className="setting-group">
          <h4>🎛️ Other Settings</h4>
          <div className="toggle-settings">
            <label className="toggle-label">
              <input
                type="checkbox"
                checked={settings.showMotivationalQuotes}
                onChange={(e) => updateSettings({ ...settings, showMotivationalQuotes: e.target.checked })}
              />
              <span>💡 Show motivational quotes</span>
            </label>

            <label className="toggle-label">
              <input
                type="checkbox"
                checked={settings.dimBackground}
                onChange={(e) => updateSettings({ ...settings, dimBackground: e.target.checked })}
              />
              <span>🌙 Dim background</span>
            </label>

            <label className="toggle-label">
              <input
                type="checkbox"
                checked={settings.blockingEnabled}
                onChange={(e) => updateSettings({ ...settings, blockingEnabled: e.target.checked })}
              />
              <span>🚫 Enable website blocking (simulated)</span>
            </label>
          </div>
        </div>

        {settings.blockingEnabled && <BlockedSitesList />}
      </div>

      {isActive && (
        <div className="focus-status">
          <div className="status-indicator">
            <div className="pulse"></div>
            <span>Focus Mode Active</span>
          </div>
          <div className="active-settings">
            <span>Theme: {themes.find(t => t.id === settings.theme)?.name}</span>
            {settings.ambientSound !== 'none' && (
              <span>Sound: {ambientSounds.find(s => s.id === settings.ambientSound)?.name}</span>
            )}
          </div>
        </div>
      )}

      <div className="focus-tips">
        <h4>💡 Focus Tips</h4>
        <ul>
          <li>Turn off notifications on your device</li>
          <li>Keep a glass of water nearby</li>
          <li>Use the Pomodoro technique for optimal focus</li>
          <li>Take regular breaks to avoid burnout</li>
          <li>Set specific goals for each study session</li>
        </ul>
      </div>
    </div>
  );
};

export default FocusMode;